"use client";

import { useCallback, useState } from "react";
import { LuSearch } from "react-icons/lu";

import { Command, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { useUserSearch } from "@/hooks/use-user-search";
import type { PartialAccount } from "@/utils/types";

import { Spinner } from "../spinner";
import { UserResult } from "./user-result";

interface GlobalSearchProps {
  placeholder?: string;
  baseRedirectUrl?: string;
  disabled?: boolean;
  onSelect?: (account: PartialAccount) => void;
}

const UserSearch = ({
  placeholder = "Search any user...",
  baseRedirectUrl = "/",
  onSelect,
}: GlobalSearchProps) => {
  const [searchValue, setSearchValue] = useState<string>("");
  const {
    loading: loadingSearchResults,
    searchResults,
    search,
    aborterRef,
  } = useUserSearch(searchValue);

  const resetSearch = useCallback(() => {
    setSearchValue("");
  }, []);

  return (
    <form className="relative flex w-full items-center">
      <Command shouldFilter={false} className="rounded-full text-left">
        <Input
          placeholder={placeholder}
          className="pr-10"
          value={searchValue}
          onInput={(event) => {
            setSearchValue(event.currentTarget.value);
          }}
        />

        <CommandList
          className="
            absolute top-[40px] z-100 mt-1 max-h-[300px] w-full
            overflow-x-hidden overflow-y-auto rounded-xl bg-input
          "
        >
          {loadingSearchResults && (
            <Command className="rounded-xl border">
              <Spinner size={32} className="mx-auto my-2" />
            </Command>
          )}

          {searchResults.accounts.length > 0 && (
            <div className="rounded-xl border">
              {searchResults.accounts.map((account) => {
                return (
                  <UserResult
                    key={account.publicKey}
                    {...{
                      onSelect,
                      baseRedirectUrl: baseRedirectUrl,
                      account,
                      resetSearch,
                    }}
                  />
                );
              })}
            </div>
          )}
        </CommandList>
      </Command>

      <LuSearch
        className="absolute top-[11px] right-4 size-4 cursor-pointer text-muted"
        onClick={() => search(searchValue, aborterRef)}
      />
    </form>
  );
};

export default UserSearch;
